<template>
  <div class="forgot-pass">
    <div class="description">
      {{ enterContactVerify }}
    </div>

    <authorization-tabs
      v-model:tabs="tabs"
      @update:selected-tab="selectedTab = $event"
    >
      <template #email>
        <form-input-text
          v-model:value="forgotFormData.email"
          :hint="setError('email')"
          type="email"
          is-required
          label=""
          name="email"
          :placeholder="getContent(fieldsSettings, defaultLocaleFieldsSettings, `fieldsControls.phone.email`)"
          @blur="v$.email?.$touch()"
          @focus="onFocus('email')"
        />
      </template>

      <template #phone>
        <form-input-phone
          v-model:value="forgotFormData.phone"
          :placeholder="getContent(fieldsSettings, defaultLocaleFieldsSettings, `fieldsControls.phone.placeholder`)"
          is-required
          :hint="setError('phone')"
          :country-code="countryOrigin"
        />
      </template>
    </authorization-tabs>
  </div>
</template>

<script setup lang="ts">
import { replaceProjectName } from '@skeleton/utils/contentReplace';

const { settingsConstants } = storeToRefs(useGlobalStore());
const { countryOrigin } = storeToRefs(useAuthorizationStore());
const { showAlert } = useLayoutStore();
const { getFormRules, getContent, createValidationRules } = useProjectMethods();
const {
  popupsData,
  defaultLocalePopupsData,
  defaultLocaleAlertsData,
  alertsData,
  fieldsSettings,
  defaultLocaleFieldsSettings,
} = storeToRefs(useGlobalStore());

const tabs = ref([
  {
    title: 'Email',
    isActive: false,
    key: 'email',
    icon: 'message',
    canShow: true,
  },
  {
    title: 'Phone',
    isActive: false,
    key: 'phone',
    icon: 'phone',
    canShow: false,
  },
]);

const selectedTab = ref('email');

const formValidationRules = ref({});
const forgotFormData = reactive({ email: '', phone: '' });

const formRules = computed(() => getFormRules(formValidationRules.value));
const { serverFormErrors, v$, onFocus, setError } = useFormValidation(formRules, forgotFormData);

watchEffect(() => {
  const validationRules = [{ name: selectedTab.value, isRequired: true }];

  formValidationRules.value = createValidationRules(validationRules, true);
});

const { forgotProfilePassword } = useCoreProfileApi();
const isProcessing = ref(false);

const validateForm = async (): Promise<void> => {
  v$.value.$reset();
  const validFormData = await v$.value.$validate();
  if (!validFormData) return;

  if (selectedTab.value === 'email') {
    await sendEmail();
  }
  else {
    emit('phoneValidated', forgotFormData.phone);
  }
};

const sendEmail = async (): Promise<void> => {
  try {
    isProcessing.value = true;
    await forgotProfilePassword(forgotFormData);
    emit('emailSent', forgotFormData.email);
  }
  catch (error: any) {
    if (error.response?.status === 422) {
      serverFormErrors.value = error.data?.error?.fields;
    }
    else {
      showAlert(
        alertsData.value?.profile?.emailIsNotRegistered
        || defaultLocaleAlertsData.value?.profile?.emailIsNotRegistered,
        'error',
      );
    }
    emit('error');
  }
  finally {
    isProcessing.value = false;
  }
};

const toggleTab = (tabKey: 'email' | 'phone', canShow: boolean) => {
  const foundTab = tabs.value.find(tab => tab.key === tabKey);
  if (foundTab) foundTab.canShow = canShow;
};

const enterContactVerify = computed(() => {
  return replaceProjectName(getContent(popupsData, defaultLocalePopupsData, 'authorization.enterContactVerify'));
});

const emit = defineEmits([
  'close',
  'phoneValidated',
  'error',
  'emailSent',
  'update:isProcessing',
  'update:selectedTab',
]);

defineExpose({
  validateForm,
});

watch(isProcessing, (newValue) => {
  emit('update:isProcessing', newValue);
});

watch(selectedTab, (newValue) => {
  emit('update:selectedTab', newValue);
});

watchEffect(() => {
  const canAuthorizeWithEmail = !!settingsConstants?.value?.player?.registration?.email;
  toggleTab('email', canAuthorizeWithEmail);

  const canAuthorizeWithPhone = !!settingsConstants?.value?.player?.registration?.phone;
  toggleTab('phone', canAuthorizeWithPhone);
});
</script>

<style src="~/assets/styles/components/modal-content/forgot-pass.scss" lang="scss" />
